import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  BellIcon,
  SunIcon,
  MoonIcon,
} from "@heroicons/react/24/solid";
import axios from "axios";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

import ProfilePic from "../../../assets/images/blank-profile-pic.png";
import Logo from "../../../assets/images/un_resume_logo2.png";
import { useTheme } from "../../constants/ThemeProvider";

import Notifications from "./Notifications";

export default function HomeNavbar() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const currentPath = location.pathname;
  const [showNotifications, setShowNotifications] = useState(false);
  const [userData, setUserData] = useState({ name: "", profile_picture: "" });

  const toggleNotifications = () => setShowNotifications(!showNotifications);

  const handleLogout = async () => {
    try {
      const csrfToken = Cookies.get("csrftoken");
      await axios.post("/api/logout/", null, {
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "application/json",
        },
      });
      navigate("/login");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  useEffect(() => {
    const userId = Cookies.get("id");
    if (!userId) {
      console.error("User ID not found");
      return;
    }
    axios
      .get(`/api/users/${userId}/`, { withCredentials: true })
      .then((response) => {
        const { name, profile_picture } = response.data;
        setUserData({ name, profile_picture });
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
      });
  }, []);

  return (
    <nav className="border-b border-b-gray-300 dark:border-b-gray-700 fixed top-0 left-0 right-0 z-50 flex justify-between items-center h-16 px-4 sm:px-6 lg:px-8 transition-all duration-300 bg-white dark:bg-gray-900 w-full">
      {/* Logo */}
      <div className="flex items-center gap-2">
        <img
          alt="UnResume Logo"
          className="w-9 h-9 rounded-full shadow-sm"
          src={Logo}
        />
        <Link className="text-xl font-bold text-gray-900 dark:text-gray-100 transition-colors duration-200 tracking-tighter">
          UnResumé
        </Link>
        <div className="hidden ml-6 md:flex flex-row gap-x-6">
          <div
            className={`h-16 flex items-center ${currentPath === "/home" ? "border-b-3 border-blue-700" : ""}`}
          >
            <Link
              className={`text-sm font-medium transition-colors duration-200 ${
                currentPath === "/home"
                  ? "text-blue-600 dark:text-blue-400"
                  : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
              }`}
              to="/home"
            >
              Matches
            </Link>
          </div>

          <div
            className={`h-16 flex items-center ${currentPath === "/leaderboard" ? "border-b-3 border-blue-700" : ""}`}
          >
            <Link
              className={`text-sm font-medium transition-colors duration-200 ${
                currentPath === "/leaderboard"
                  ? "text-blue-600 dark:text-blue-400"
                  : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
              }`}
              to="/leaderboard"
            >
              League
            </Link>
          </div>

          <div
            className={`h-16 flex items-center ${currentPath === "/explore" ? "border-b-3 border-blue-700" : ""}`}
          >
            <Link
              className={`text-sm font-medium transition-colors duration-200 ${
                currentPath === "/explore"
                  ? "text-blue-600 dark:text-blue-400 font-semibold"
                  : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
              }`}
              to="/explore"
            >
              Explore
            </Link>
          </div>
          <div
            className={`h-16 flex items-center ${currentPath === "/resources" ? "border-b-3 border-blue-700" : ""}`}
          >
            <Link
              className={`text-sm font-medium transition-colors duration-200 ${
                currentPath === "/resources"
                  ? "text-blue-600 dark:text-blue-400 font-semibold"
                  : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
              }`}
              to="/resources"
            >
              Resources
            </Link>
          </div>
        </div>
      </div>

      {/* Desktop Menu */}
      <div className="hidden md:flex items-center gap-2">
        {/* Theme toggle */}
        <button
          aria-label={
            theme === "dark" ? "Switch to light mode" : "Switch to dark mode"
          }
          className="p-2 rounded-full hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none"
          type="button"
          onClick={toggleTheme}
        >
          {theme === "dark" ? (
            <SunIcon className="w-5 h-5 text-gray-300" />
          ) : (
            <MoonIcon className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {/* Notifications */}
        <button
          className="p-2 rounded-full hover:bg-blue-100 transition-colors duration-200 focus:outline-none"
          onClick={toggleNotifications}
        >
          <BellIcon className="w-6 h-6 text-gray-400" />
        </button>

        {/* Profile Dropdown */}
        <Menu as="div" className="relative">
          <MenuButton className="flex items-center gap-2 p-2 rounded-full hover:bg-blue-100 transition-colors duration-200 focus:outline-none">
            <img
              alt="Profile Pic"
              className="w-8 h-8 rounded-full border-2 border-blue-200"
              src={userData.profile_picture || ProfilePic}
            />
            <ChevronDownIcon className="w-4 h-4 text-blue-600" />
          </MenuButton>
          <Transition
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <MenuItems className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 shadow-lg focus:outline-none rounded-lg py-2 ring-1 ring-blue-200/20 dark:ring-gray-700">
              <MenuItem>
                {({ active }) => (
                  <Link
                    className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}`}
                    to="/profile"
                  >
                    Profile
                  </Link>
                )}
              </MenuItem>
              <MenuItem>
                {({ active }) => (
                  <button
                    className={`block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 ${active ? "bg-red-50 dark:bg-red-900/30" : ""}`}
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                )}
              </MenuItem>
            </MenuItems>
          </Transition>
        </Menu>
      </div>

      {/* Mobile Menu */}
      <Menu as="div" className="relative md:hidden flex flex-row gap-x-2">
        <button
          aria-label={
            theme === "dark" ? "Switch to light mode" : "Switch to dark mode"
          }
          className="p-2 rounded-full hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none"
          type="button"
          onClick={toggleTheme}
        >
          {theme === "dark" ? (
            <SunIcon className="w-5 h-5 text-gray-300" />
          ) : (
            <MoonIcon className="w-5 h-5 text-gray-400" />
          )}
        </button>
        <button
          className="p-2 rounded-full hover:bg-blue-100 transition-colors duration-200 focus:outline-none"
          onClick={toggleNotifications}
        >
          <BellIcon className="w-6 h-6 text-gray-400" />
        </button>
        <MenuButton className="flex items-center gap-2 p-2 rounded-full hover:bg-blue-100 transition-colors duration-200 focus:outline-none">
          <img
            alt="Profile Pic"
            className="w-8 h-8 rounded-full border-2 border-blue-200"
            src={userData.profile_picture || ProfilePic}
          />
          <ChevronDownIcon className="w-4 h-4 text-blue-600" />
        </MenuButton>
        <Transition
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <MenuItems className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 shadow-lg focus:outline-none rounded-lg py-2 ring-1 ring-blue-200/20 dark:ring-gray-700">
            <MenuItem>
              {({ active }) => (
                <Link
                  className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}`}
                  to="/home"
                >
                  Matches
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <Link
                  className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}`}
                  to="/leaderboard"
                >
                  League
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <Link
                  className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}`}
                  to="/explore"
                >
                  Explore
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <Link
                  className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}`}
                  to="/resources"
                >
                  Resources
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <Link
                  className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}`}
                  to="/profile"
                >
                  Profile
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  className={`block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 ${active ? "bg-red-50 dark:bg-red-900/30" : ""}`}
                  onClick={handleLogout}
                >
                  Logout
                </button>
              )}
            </MenuItem>
          </MenuItems>
        </Transition>
      </Menu>

      {/* Notifications */}
      <Transition
        enter="transition ease-out duration-300"
        enterFrom="opacity-0 translate-x-10"
        enterTo="opacity-100 translate-x-0"
        leave="transition ease-in duration-200"
        leaveFrom="opacity-100 translate-x-0"
        leaveTo="opacity-0 translate-x-10"
        show={showNotifications}
      >
        <div className="fixed z-[9999] top-16 right-4 w-80 max-w-xs mt-2 h-[90vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-auto border border-blue-200/50 dark:border-gray-700">
          <Notifications onClose={toggleNotifications} />
        </div>
      </Transition>
    </nav>
  );
}
